import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NextLink from 'next/link';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { LineItem } from 'shared/types/wishlist/LineItem';
import OutOfStock from 'components/commercetools-ui/atoms/out-of-stock';
import QuickView from 'components/commercetools-ui/organisms/product/product-quick-view';
import Prices from 'components/commercetools-ui/organisms/product/product-tile/prices';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { desktop } from 'helpers/utils/screensizes';
import Image from 'frontastic/lib/image';
import useTrack from './useTrack';
import { ShareIcon } from '@heroicons/react/24/outline';
import { ITEM_CODE, ITEM_NAME } from 'helpers/constants/attributes';
import ColorSwatches from './swatches';
import usePath from 'helpers/hooks/usePath';
import { useParams } from 'next/navigation';
import Button from 'components/commercetools-ui/atoms/button';
import ShopOnline from '../shop-online';
import { localeMapping } from 'helpers/constants/localeMapping';
import useWindowWidth from 'helpers/hooks/useBrowerWidth';
import { getPdpUrl } from 'helpers/utils/pdpUrlHelper';

interface ProductTileProps {
  product: Product;
  onClick?: () => void;
  isSearchResult?: boolean;
  isCollectionPage?: boolean;
  isCategoryPage?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  images?: string[];
}

const ProductTiles: FC<ProductTileProps> = ({
  product,
  images,
  onClick,
  isSearchResult = false,
  isCollectionPage = false,
  isCategoryPage = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
}) => {
  const { locale } = useParams();
  const localeKey = localeMapping[locale];
  const [isDesktopSize] = useMediaQuery(desktop);
  const { ref } = useTrack({ product });
  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;
  const [selectedVariant, setSelectedVariant] = useState(
    () => product?.variants?.filter((variant) => variant.id === product?.selectedVariant?.id)?.[0] || product.variants?.[0],
  );
  const width = useWindowWidth();
  const hasDiscount =
    selectedVariant?.discountedPrice?.centAmount &&
    selectedVariant?.price?.centAmount &&
    selectedVariant?.discountedPrice?.centAmount < selectedVariant?.price?.centAmount;

  const discountedPrice = selectedVariant?.discountedPrice;
  const { path } = usePath();
  const discountPercentage = selectedVariant
    ? (((selectedVariant.price?.centAmount as number) - (discountedPrice?.centAmount as number)) /
        (selectedVariant.price?.centAmount as number)) *
      100
    : 0;

  const [imageHovered, setImageHovered] = useState(false);
  const [buttonHovered, setButtonHovered] = useState(false);
  const buttonIsVisible = useMemo(
    () => (imageHovered || buttonHovered) && isDesktopSize,
    [imageHovered, buttonHovered, isDesktopSize],
  );
  const hideButton = () => {
    setImageHovered(false);
    setButtonHovered(false);
  };
  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);

  
  const productUrl = useMemo(() => {
    if (!product._url) return getPdpUrl(product.slug, selectedVariant?.sku, selectedVariant?.attributes?.colour);

    return (
      product._url +
      (isSearchResult ? '?sr=1' : '') +
      (isCategoryPage ? '?category=' + path : '') +
      (isCollectionPage ? '?collection=' + path : '')
    );
  }, [product, selectedVariant, isSearchResult]);

  const buttonClass = useMemo(() => {
    if (path.includes('/renovation-guide')) {
      return width !== null && width < 361 ? 'flex-col' : 'items-center justify-between';
    } else {
      return 'items-center justify-between';
    }
  }, [path, width]);
  
  return (
    <div>
      <div onClick={onClick} ref={ref}>
         <NextLink href={`/${locale}${productUrl}`}> 
          <div className="relative">
            <div
              className="relative w-full"
              onMouseEnter={() => setImageHovered(true)}
              onMouseLeave={() => setImageHovered(false)}
            >
              <div className="relative block w-full xl:h-[10rem] 2xl:h-[23rem]" style={{ paddingBottom: '100%' }}>
                <Image
                  priority
                  src={
                    selectedVariant?.images?.[0]
                      ? encodeURI(selectedVariant?.images?.[0].toString())
                      : '/images/no-image-placeholder/product-thumbnail.jpg'
                  }
                  alt={product.name ?? ''}
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                  className="w-full rounded-sm group-hover:opacity-75 "
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  onError={(event: any) => {
                    event.target.id = '/images/no-image-placeholder/search-result-thumbnail.png';
                    event.target.srcset = '/images/no-image-placeholder/search-result-thumbnail.png';
                  }}
                />
              </div>
            </div>
          </div>

          <div
            className={`h-[12rem] ${
              imageHovered ? 'bg-[#F3EDE9]' : isCollectionPage ? 'bg-white' : 'bg-[#F8F8F8]'
            } ] 2xl:px-24 xl:px-24 lg:px-24 md:px-24  px-16  pt-16 hover:bg-[#F3EDE9]`}
          >

            {/* Change textsize to 11. because some name lenth was big */}
            <div className="flex h-[8rem] flex-col justify-between 2xl:h-[9rem]">
              <div className="flex items-center justify-between">
                <span className="text-11 font-normal leading-6 text-[#333] md:text-12 2xl:text-14">
                  {product?.categories &&
                    (product?.categories[0]?.name ||
                      product?.categories?.[1]?.categoryId ||
                      product?.categories[0]?.categoryId)}
                </span>
              </div>
              
              {/* Added ShopOnline button in all product tile. This is only for Caroma NZ and not applicable for Caroma AU */}
              <div className="flex items-center justify-between">
                <span className="flex cursor-pointer text-14 font-normal leading-6 text-[#333] md:text-12 2xl:text-14"
                  onClick={(e) => e.preventDefault()}>
                  <ShopOnline itemCode={product?.variants?.[0]?.attributes?.[ITEM_CODE].toString()} />
                </span>
              </div> 
              
              <div className="block max-w-[80%]  md:max-w-[100%] ">
                <p className="line-clamp-2 text-18 font-medium capitalize leading-[1.625rem] text-[#2B2C39] md:text-16 lg:text-primary-black  2xl:text-18">
                  {product?.attributesAlgolia?.[ITEM_NAME]?.[localeKey] || product?.name}
                </p>
              </div>
              <div className={`mt-4 flex gap-2 md:gap-0 lg:gap-0 xl:gap-0 2xl:gap-0  ${buttonClass} text-12 font-medium text-[#090909] md:text-14 2xl:text-16`}>
                <span>
                  <span className="text-[#666]">Product code</span>{' '}
                  {product?.attributesAlgolia?.[ITEM_CODE] || selectedVariant?.attributes?.[ITEM_CODE]}
                </span>
              </div>
              <div>
                <span className="flex gap-8">
                  {product?.variants && (
                    <ColorSwatches
                      product={product}
                      variants={product?.variants}
                      setSelectedVariant={setSelectedVariant}
                    />
                  )}
                </span>
              </div>
            </div>
          </div>
        </NextLink>
      </div>
    </div>
  );
};

export default ProductTiles;
